// Projects Management Page - Clean & Minimal

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { getProjects, getCustomers, createProject, updateProject, deleteProject } from '@/lib/api';
import type { Project, ProjectFormData, Customer } from '@/lib/types';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import ProjectList from '@/components/projects/ProjectList';
import ProjectForm from '@/components/projects/ProjectForm';
import ProjectIntegrationModal from '@/components/projects/ProjectIntegrationModal';
import Modal from '@/components/ui/Modal';
import toast from 'react-hot-toast';

export default function ProjectsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [selectedProjectForIntegration, setSelectedProjectForIntegration] = useState<Project | null>(null);
  
  const selectedCustomerId = searchParams.get('customer') || '';

  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
  });

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects', selectedCustomerId],
    queryFn: () => getProjects(selectedCustomerId || undefined),
  });

  const createMutation = useMutation({
    mutationFn: (data: ProjectFormData) => createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Projekt erfolgreich erstellt');
      setIsModalOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Fehler beim Erstellen des Projekts');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ProjectFormData> }) =>
      updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Projekt erfolgreich aktualisiert');
      setIsModalOpen(false);
      setEditingProject(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Fehler beim Aktualisieren des Projekts');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Projekt erfolgreich gelöscht');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Fehler beim Löschen des Projekts');
    },
  });

  const handleCreate = (data: ProjectFormData) => {
    createMutation.mutate(data);
  };

  const handleUpdate = (data: ProjectFormData) => {
    if (editingProject) {
      updateMutation.mutate({ id: editingProject.id, data });
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
  };

  const handleNewProject = () => {
    setEditingProject(null);
    setIsModalOpen(true);
  };

  const handleShowIntegrationCode = (project: Project) => {
    setSelectedProjectForIntegration(project);
    setShowIntegrationModal(true);
  };

  const handleToggleActive = (project: Project) => {
    updateMutation.mutate({
      id: project.id,
      data: { active: !project.active },
    });
  };

  const handleDelete = (project: Project) => {
    if (confirm(`Projekt "${project.name}" wirklich löschen?`)) {
      deleteMutation.mutate(project.id);
    }
  };

  const handleCustomerFilterChange = (customerId: string) => {
    if (customerId) {
      setSearchParams({ customer: customerId });
    } else {
      setSearchParams({});
    }
  };

  // Gruppiere Projekte nach Kunden
  const projectsByCustomer: Record<string, { customer: Customer | null; projects: Project[] }> = {};
  
  (projects || []).forEach(project => {
    const customerId = project.customer_id || 'ohne';
    if (!projectsByCustomer[customerId]) {
      projectsByCustomer[customerId] = {
        customer: project.customer || null,
        projects: []
      };
    }
    projectsByCustomer[customerId].projects.push(project);
  });

  // Sortiere Kunden alphabetisch, "ohne Kunde" kommt am Ende
  const sortedCustomerIds = Object.keys(projectsByCustomer).sort((a, b) => {
    if (a === 'ohne') return 1;
    if (b === 'ohne') return -1;
    const nameA = projectsByCustomer[a].customer?.name || '';
    const nameB = projectsByCustomer[b].customer?.name || '';
    return nameA.localeCompare(nameB);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Projekte</h1>
          <p className="text-neutral-500 mt-1">
            {projects?.length || 0} Projekte • {sortedCustomerIds.length} {sortedCustomerIds.length === 1 ? 'Kunde' : 'Kunden'}
          </p>
        </div>

        <Button onClick={handleNewProject}>
          + Neues Projekt
        </Button>
      </div>

      {/* Filter */}
      <div className="bg-white border border-neutral-200 rounded-lg p-4">
        <Select
          label="Nach Kunde filtern"
          value={selectedCustomerId}
          onChange={(e) => handleCustomerFilterChange(e.target.value)}
          options={[
            { value: '', label: 'Alle Kunden' },
            ...(customers || []).map((c: Customer) => ({
              value: c.id,
              label: c.name,
            })),
          ]}
        />
      </div>

      {/* Projects List - Grouped by Customer */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900" />
        </div>
      ) : sortedCustomerIds.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-12 text-center">
          <p className="text-neutral-500">Keine Projekte vorhanden</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedCustomerIds.map(customerId => {
            const { customer, projects: customerProjects } = projectsByCustomer[customerId];
            
            return (
              <div key={customerId} className="space-y-3">
                {/* Customer Header */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <h2 className="text-lg font-semibold text-gray-900 px-4">
                    {customer?.name || 'Ohne Kunde'}
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({customerProjects.length} {customerProjects.length === 1 ? 'Projekt' : 'Projekte'})
                    </span>
                  </h2>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {/* Projects for this customer */}
                <ProjectList
                  projects={customerProjects}
                  isLoading={false}
                  onEdit={handleEdit}
                  onShowIntegrationCode={handleShowIntegrationCode}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDelete}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProject ? 'Projekt bearbeiten' : 'Neues Projekt'}
      >
        <ProjectForm
          project={editingProject}
          customers={customers || []}
          preselectedCustomerId={selectedCustomerId || undefined}
          onSubmit={editingProject ? handleUpdate : handleCreate}
          onCancel={handleCloseModal}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Integration Code Modal */}
      <ProjectIntegrationModal
        isOpen={showIntegrationModal}
        onClose={() => {
          setShowIntegrationModal(false);
          setSelectedProjectForIntegration(null);
        }}
        project={selectedProjectForIntegration}
      />
    </div>
  );
}
